import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar, Menu, MenuItem, menuClasses } from 'react-pro-sidebar';
import {
    Upload as SubmitIcon,
    BarChart as VisualizeIcon,
    Hub as DigitalTwinIcon,
    FlashOn as GenerateEISIcon,
    Assessment as SoHAnalysisIcon,
    AccessTime as LifetimeAnalysisIcon,
    QueryStats as Analysis2Icon,
    Verified as CertificateIcon,
    ExitToApp as ExitIcon,
    ThumbUpAlt as ValidateIcon
} from '@mui/icons-material';
import { Link, Route, Routes } from 'react-router-dom';
import Info from './layout/Info';
import SubmitBatteryData from './layout/SubmitBatteryData';
import VisualizeData from './layout/VisualizeData';
import DigitalTwin from './layout/DigitalTwin';
import ValidateDigitalTwin from './layout/ValidateDigitalTwin';
import GenerateEIS from './layout/GenerateEIS';
import BatterySoHAnalysis from './layout/BatterySoHAnalysis';
import BatteryLifetime from './layout/BatteryLifetime';
import BatteryLifetime2 from './layout/BatteryLifetime2';
import BatteryCertificate from './layout/BatteryCertificate';
import './BatteryScopeV2.css';
import logo from "../../assets/img/logos/logo-no-background.png";
import darklogo from "../../assets/img/logos/logo-black-no-bg.png";
import logoicon from "../../assets/Tab_Icon.png";
import Nav from "../../components/Navbar/Nav";
import IconButton from '@mui/material/IconButton';
import { Brightness4 as LightModeIcon, Brightness7 as DarkModeIcon, Menu as MenuIcon } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { BatteryConfigProvider } from './context/BatteryConfigContext';
import { styled } from '@mui/material/styles';

const themes = {
    light: {
        sidebar: {
            backgroundColor: '#ffffff',
            color: '#607489',
        },
        menu: {
            menuContent: '#fbfcfd',
            icon: '#FFA500',
            hover: {
                backgroundColor: '#c5e4ff',
                color: '#44596e',
            },
            disabled: {
                color: '#9fb6cf',
            },
        },
        header: {
            backgroundColor: '#000000',
            color: '#000000',
        },
    },
    dark: {
        sidebar: {
            backgroundColor: '#000000',
            color: '#ffffff',
        },
        menu: {
            menuContent: '#082440',
            icon: '#FFA500',
            hover: {
                backgroundColor: '#00458b',
                color: '#b6c8d9',
            },
            disabled: {
                color: '#3e5e7e',
            },
        },
        header: {
            backgroundColor: '#000000',
            color: '#ffffff',
        },
    },
};

const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const StyledMenuItem = styled(MenuItem)({
    fontWeight: "500",
    fontSize: "14px",
    fontFamily: '"Bai Jamjuree", sans-serif',
});

const pages = [
    "/batteryscope/submit",
    "/batteryscope/visualize",
    "/batteryscope/digital-twin",
    "/batteryscope/validate-digital-twin",
    "/batteryscope/generate-eis",
    "/batteryscope/soh-analysis",
    "/batteryscope/lifetime-analysis",
    "/batteryscope/lifetime-analysis-2",
    "/batteryscope/certificate",
];

const visitedPages = [];
const isPageActive = (pathname, page) => {
    if(visitedPages.includes(page)){
        return true;
    }
    return pages.indexOf(pathname) >= pages.indexOf(page);
};


const BatteryScopeV2 = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [toggled, setToggled] = useState(false);
    const [rtl, setRtl] = useState(false);
    const [hasImage, setHasImage] = useState(true);
    const [theme, setTheme] = useState('dark');
    const [dataSubmitted, setDataSubmitted] = useState(null);
    const [csvData, setCsvData] = useState(null);
    const { pathname } = useLocation();

    const menuItemStyles = {
        root: {
            fontSize: '13px',
            fontWeight: 400,
            fontFamily: "'Bai Jamjuree', sans-serif",
        },
        icon: {
            color: themes[theme].menu.icon,
            [`&.${menuClasses.disabled}`]: {
                color: themes[theme].menu.disabled.color,
            },
        },
        SubMenuExpandIcon: {
            color: '#b6b7b9',
        },
        subMenuContent: ({ level }) => ({
            backgroundColor:
                level === 0
                    ? hexToRgba(themes[theme].menu.menuContent, hasImage && !collapsed ? 0.4 : 1)
                    : 'transparent',
            fontFamily: "'Bai Jamjuree', sans-serif",
        }),
        button: {
            [`&.${menuClasses.disabled}`]: {
                color: themes[theme].menu.disabled.color,
            },
            '&:hover': {
                backgroundColor: hexToRgba(themes[theme].menu.hover.backgroundColor, hasImage ? 0.8 : 1),
                color: themes[theme].menu.hover.color,
            },
            fontFamily: "'Bai Jamjuree', sans-serif",
        },
        label: ({ open }) => ({
            fontWeight: open ? 600 : undefined,
            fontFamily: "'Bai Jamjuree', sans-serif",
        }),
    };

    useEffect(() => {
        if(pathname){
            visitedPages.push(pathname);
        }
    }, [pathname]); 

    const handleThemeChange = () => {
        setTheme(theme === 'dark' ? 'light' : 'dark');
    };

    return (
        <div style={{ display: 'flex', height: '100vh', flexDirection: 'column', direction: rtl ? 'rtl' : 'ltr' }}>
            {/* <Nav /> */}

            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                <Sidebar
                    collapsed={collapsed}
                    toggled={toggled}
                    onBackdropClick={() => setToggled(false)}
                    onBreakPoint={() => setToggled(!toggled)}
                    // image={hasImage ? "https://user-images.githubusercontent.com/25878302/144499035-2911184c-76d3-4611-86e7-bc4e8ff84ff5.jpg" : undefined}
                    rtl={rtl}
                    breakPoint="md"
                    backgroundColor={hexToRgba(themes[theme].sidebar.backgroundColor, hasImage ? 1 : 1)}
                    rootStyles={{
                        color: themes[theme].sidebar.color,
                        fontFamily: 'Bai Jamjuree',
                    }}
                >
                    <div style={{ textAlign: 'center', margin: '20px 20px', padding: '10px 0px' }} onClick={() => window.location.replace('/batteryscope')}>
                        {collapsed ? <img src={logoicon} alt="Logo" style={{ width: '100%', height: 'auto' }} /> : <img src={logo} alt="Logo" style={{ width: '100%', height: 'auto' }} />}
                    </div>
                    <Menu menuItemStyles={menuItemStyles}>
                        <StyledMenuItem 
                            icon={<SubmitIcon />} 
                            component={<Link to="submit" />} 
                            className={isPageActive(pathname, '/batteryscope/submit') ? 'active' : ''}
                            disabled={!isPageActive(pathname, '/batteryscope/submit')}
                        >
                            Submit Battery Data
                        </StyledMenuItem>
                        <StyledMenuItem 
                            icon={<VisualizeIcon />} 
                            component={<Link to="visualize" />} 
                            className={isPageActive(pathname, '/batteryscope/visualize') ? 'active' : ''}
                            disabled={!isPageActive(pathname, '/batteryscope/visualize')}
                        >
                            Visualize Battery Data
                        </StyledMenuItem>
                        <StyledMenuItem 
                            icon={<DigitalTwinIcon />} 
                            component={<Link to="digital-twin" />} 
                            className={isPageActive(pathname, '/batteryscope/digital-twin') ? 'active' : ''}
                            disabled={!isPageActive(pathname, '/batteryscope/digital-twin')}
                        >
                            Digital Twin
                        </StyledMenuItem>
                        <StyledMenuItem 
                            icon={<ValidateIcon />} 
                            component={<Link to="validate-digital-twin" />} 
                            className={isPageActive(pathname, '/batteryscope/validate-digital-twin') ? 'active' : ''}
                            disabled={!isPageActive(pathname, '/batteryscope/validate-digital-twin')}
                        >
                            Validate Digital Twin
                        </StyledMenuItem>
                        <StyledMenuItem 
                            icon={<GenerateEISIcon />} 
                            component={<Link to="generate-eis" />} 
                            className={isPageActive(pathname, '/batteryscope/generate-eis') ? 'active' : ''}
                            disabled={!isPageActive(pathname, '/batteryscope/generate-eis')}
                        >
                            EIS Health Signatures
                        </StyledMenuItem>
                        <StyledMenuItem 
                            icon={<SoHAnalysisIcon />} 
                            component={<Link to="soh-analysis" />} 
                            className={isPageActive(pathname, '/batteryscope/soh-analysis') ? 'active' : ''}
                            disabled={!isPageActive(pathname, '/batteryscope/soh-analysis')}
                        >
                            SoH Analysis
                        </StyledMenuItem>
                        <StyledMenuItem 
                            icon={<LifetimeAnalysisIcon />} 
                            component={<Link to="lifetime-analysis" />} 
                            className={isPageActive(pathname, '/batteryscope/lifetime-analysis') ? 'active' : ''}
                            disabled={!isPageActive(pathname, '/batteryscope/lifetime-analysis')}
                        >
                            Life-Time Plots
                        </StyledMenuItem>
                        <StyledMenuItem 
                            icon={<Analysis2Icon />} 
                            component={<Link to="lifetime-analysis-2" />} 
                            className={isPageActive(pathname, '/batteryscope/lifetime-analysis-2') ? 'active' : ''}
                            disabled={!isPageActive(pathname, '/batteryscope/lifetime-analysis-2')}
                        >
                            Life-Time Analysis
                        </StyledMenuItem>
                        <StyledMenuItem 
                            icon={<CertificateIcon />} 
                            component={<Link to="certificate" />} 
                            className={isPageActive(pathname, '/batteryscope/certificate') ? 'active' : ''}
                            disabled={!isPageActive(pathname, '/batteryscope/certificate')}
                        >
                            Health Certificate
                        </StyledMenuItem>
                    </Menu>
                </Sidebar>
                <div style={{ flex: 1, padding: 20, overflow: 'hidden', backgroundColor: themes[theme].sidebar.backgroundColor }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', borderBottom: '1px solid #dedede', paddingBottom: '15px' }}>
                        <IconButton onClick={() => setCollapsed(!collapsed)} style={{ color: themes[theme].header.color }}>
                            <MenuIcon />
                        </IconButton>
                        <h1 className="text-xl text-[#e8442d] text-center md:text-2xl lg:text-2xl">
                            BatteryScope-P: Battery Health Monitoring and Analysis
                        </h1>
                        <div style={{ display: 'flex' }}>
                            {/* <IconButton onClick={handleThemeChange} style={{ color: themes[theme].header.color }}>
                            {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                        </IconButton> */}
                            {/* ExitIcon */}
                            <IconButton style={{ color: themes[theme].header.color }} onClick={() => window.location.replace('/')}>
                                <ExitIcon />
                            </IconButton>
                        </div>
                    </div>
                    {/* <BatteryConfigProvider> */}
                    <Routes>
                        <Route path="submit" element={<SubmitBatteryData setDataSubmitted={setDataSubmitted} csvData={csvData} setCsvData={setCsvData} />} />
                        <Route path="visualize" element={<VisualizeData dataSubmitted={dataSubmitted} csvData={csvData} />} />
                        <Route path="digital-twin" element={<DigitalTwin />} />
                        <Route path="validate-digital-twin" element={<ValidateDigitalTwin />} />
                        <Route path="generate-eis" element={<GenerateEIS />} />
                        <Route path="soh-analysis" element={<BatterySoHAnalysis />} />
                        <Route path="lifetime-analysis" element={<BatteryLifetime />} />
                        <Route path="lifetime-analysis-2" element={<BatteryLifetime2 />} />
                        <Route path="certificate" element={<BatteryCertificate />} />
                        <Route path="/" element={<Info />} />
                    </Routes>
                    {/* </BatteryConfigProvider> */}
                </div>
            </div>
        </div>
    );
};

export default BatteryScopeV2;
